import React, { ReactElement, useEffect, useMemo, useState } from "react";
import {
  makeStyles,
  Button,
  CircularProgress,
  TextField,
  Theme,
  createStyles,
  Dialog,
  IconButton,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import ActionDialog from "../../common/ActionDialog";
import { RedirectUri, useEnumStrings } from "../../enumStrings";
import {
  GetAllRewardCampaignsForChamberDocument,
  RewardCampaignInput,
  useCreateRewardCampaignMutation,
  useGetAllChamberMembersQuery,
  UserType,
} from "../../../graphql/types";
import "react-toastify/dist/ReactToastify.css";
import { useFormik } from "formik";
import * as yup from "yup";
import useRedirect from "../../common/Hooks/useRedirect";
import useGetChamberFromUserIdInAuth from "../../common/Hooks/useGetChamberFromUserIdInAuth";
import RewardCampaignEndDatePicker from "./RewardCampaignEndDatePicker";
import Loader from "../../common/Loader/Loader";
import ChamberMemberSelector from "../ChamberMemberAdmin/ChamberMemberSelector";
import VideoTutorialButton from "../../common/VideoTutorialButton";
import ExportButton from "../../common/ExportButton";
import { openInstructionPdf } from "../../common/openInstructionPdf";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import theme from "../../../themes";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      background: "#E1A731",
      minHeight: "100vh",
      fontFamily: "Inter, sans-serif",
      overflowX: "hidden" as const,
    },
    header: {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      padding: "30px 40px",
      gap: "8px",
      background: "#E1A731",
      [theme.breakpoints.down("sm")]: {
        padding: "20px 16px",
      },
    },
    headerTopRow: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      width: "100%",
    },
    pageTitle: {
      fontFamily: "Inter, sans-serif",
      fontStyle: "normal",
      fontWeight: "bold",
      fontSize: "32px",
      lineHeight: "150%",
      display: "flex",
      alignItems: "center",
      color: "white",
      margin: 0,
      [theme.breakpoints.down("sm")]: {
        fontSize: "22px",
      },
    },
    pageSubtitle: {
      fontFamily: "Inter, sans-serif",
      fontStyle: "normal",
      fontWeight: 500,
      fontSize: "18px",
      lineHeight: "150%",
      display: "flex",
      alignItems: "center",
      color: "rgba(255, 255, 255, 0.9)",
      margin: 0,
      marginTop: "8px",
      [theme.breakpoints.down("sm")]: {
        fontSize: "16px",
        lineHeight: "1.55",
      },
    },
    videoButtons: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      gap: "21px",
      marginTop: "10px",
      marginBottom: "10px",
      [theme.breakpoints.down("sm")]: {
        flexDirection: "column",
        alignItems: "stretch",
        gap: "8px",
        width: "100%",
        "& > div": { width: "100%" },
        "& > div > a, & > div > a > button, & > div > button, & > button, & > a": {
          width: "100%",
          marginTop: 0,
        },
      },
    },
    contentContainer: {
      padding: "0 40px 40px 40px",
      background: "#E1A731",
      [theme.breakpoints.down("sm")]: {
        padding: 0,
      },
    },
    contentCard: {
      background: "#FFFFFF",
      borderRadius: "20px",
      padding: "30px 41px",
      width: "100%",
      minHeight: "600px",
      boxSizing: "border-box" as const,
      [theme.breakpoints.down("sm")]: {
        padding: "12px",
        minHeight: 0,
        borderRadius: 0,
      },
    },
    formFrame: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "center",
      alignItems: "flex-start",
      padding: "40px 51px 49px",
      gap: "10px",
      width: "900px",
      maxWidth: "100%",
      background: "#FFFFFF",
      border: "0.7px solid #888888",
      borderRadius: "31px",
      margin: "0 auto",
      boxSizing: "border-box" as const,
      [theme.breakpoints.down("sm")]: {
        padding: "12px",
        width: "100%",
        borderRadius: 0,
        border: "none",
        flexDirection: "column",
      },
    },
    form: {
      width: "798px",
      maxWidth: "100%",
      [theme.breakpoints.down("sm")]: {
        width: "100%",
      },
    },
    fieldContainer: {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      padding: "0px",
      gap: "4px",
      marginBottom: "30px",
      width: "100%",
    },
    fieldLabelRow: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "flex-start",
      padding: "0px",
      width: "100%",
    },
    fieldLabel: {
      fontFamily: "Inter, sans-serif",
      fontStyle: "normal",
      fontWeight: 600,
      fontSize: "18px",
      lineHeight: "18px",
      color: "#000000",
      display: "flex",
      alignItems: "center",
      [theme.breakpoints.down("sm")]: {
        fontSize: "15px",
      },
    },
    requiredAsterisk: {
      color: "#ED1C24",
      marginLeft: "2px",
    },
    helperText: {
      fontFamily: "Inter, sans-serif",
      fontStyle: "normal",
      fontWeight: 400,
      fontSize: "16px",
      lineHeight: "18px",
      color: "#000000",
      marginTop: 0,
      marginLeft: 0,
      marginRight: 0,
      marginBottom: "10px",
      [theme.breakpoints.down("sm")]: {
        fontSize: "13px",
        lineHeight: "20px",
      },
    },
    inputField: {
      width: "100%",
      "& .MuiOutlinedInput-root": {
        borderRadius: "8px",
        backgroundColor: "#FFFFFF",
        height: "56px",
        "& fieldset": {
          borderColor: "#CCCCCC",
          borderWidth: "1px",
        },
        "&:hover fieldset": {
          borderColor: "#999999",
        },
        "&.Mui-focused fieldset": {
          borderColor: "#000000",
          borderWidth: "2px",
        },
        "&.MuiOutlinedInput-multiline": {
          height: "auto",
          minHeight: "180px",
          alignItems: "flex-start",
        },
      },
      "& .MuiInputBase-input": {
        padding: "16px",
        fontFamily: "Inter, sans-serif",
        fontStyle: "normal",
        fontWeight: 400,
        fontSize: "16px",
        lineHeight: "24px",
        color: "#333333",
      },
      "& .MuiInputBase-inputMultiline": {
        padding: "16px",
      },
    },
    inputFieldError: {
      "& .MuiOutlinedInput-root": {
        "& fieldset": {
          borderColor: "#ED1C24",
          borderWidth: "2px",
        },
      },
    },
    errorContainer: {
      display: "flex",
      alignItems: "center",
      marginTop: "8px",
    },
    errorBadge: {
      background: "#F88585",
      border: "1px dashed #ED1C24",
      borderRadius: "4px",
      padding: "4px 12px",
      fontFamily: "Inter, sans-serif",
      fontWeight: 500,
      fontSize: "14px",
      lineHeight: "200%",
      color: "#FFFFFF",
    },
    charCounter: {
      fontFamily: "Inter, sans-serif",
      fontStyle: "normal",
      fontWeight: 400,
      fontSize: "14px",
      lineHeight: "24px",
      textAlign: "right",
      color: "#454B58",
      marginTop: "8px",
    },
    previewButton: {
      background: "#2D2D2D",
      color: "#FFFFFF",
      borderRadius: "25px",
      padding: "10px 24px",
      fontFamily: "Inter, sans-serif",
      fontWeight: 600,
      fontSize: "14px",
      textTransform: "none",
      marginTop: "15px",
      alignSelf: "flex-start",
      "&:hover": {
        background: "#1a1a1a",
      },
    },
    previewBox: {
      background: "#FFF9E6",
      border: "1px solid #FFE0B2",
      borderRadius: "10px",
      padding: "20px",
      marginTop: "15px",
    },
    previewTitle: {
      fontFamily: "Inter, sans-serif",
      fontSize: "14px",
      fontWeight: "bold",
      color: "#000000",
      marginBottom: "10px",
    },
    previewText: {
      fontFamily: "Inter, sans-serif",
      fontSize: "14px",
      color: "#000000",
      lineHeight: "1.5",
    },
    dateFieldsContainer: {
      display: "flex",
      gap: "20px",
      width: "100%",
      [theme.breakpoints.down("sm")]: {
        flexDirection: "column",
      },
    },
    dateFieldWrapper: {
      flex: 1,
    },
    submitButton: {
      background: "#E1A731",
      color: "white",
      borderRadius: "25px",
      padding: "14px 60px",
      fontFamily: "Inter, sans-serif",
      fontSize: "18px",
      fontWeight: "bold",
      textTransform: "uppercase" as const,
      letterSpacing: "1px",
      marginTop: "30px",
      width: "100%",
      maxWidth: "500px",
      display: "block",
      marginLeft: "auto",
      marginRight: "auto",
      "&:hover": {
        background: "#c99228",
      },
      "&:disabled": {
        background: "#ccc",
      },
    },
    invalidError: {
      fontFamily: "Inter, sans-serif",
      textAlign: "center",
      color: "#E1A731",
      fontSize: "14px",
      marginTop: "15px",
    },
    previewDialogPaper: {
      backgroundColor: "#E0E0E0",
      borderRadius: "20px",
      padding: "32px 40px",
      position: "relative",
      maxWidth: "640px",
      width: "100%",
      boxShadow: "none",
      overflow: "visible",
      [theme.breakpoints.down("sm")]: {
        padding: "20px 16px",
        maxWidth: "calc(100% - 24px)",
        margin: "12px",
        borderRadius: "16px",
      },
    },
    previewDialogClose: {
      position: "absolute",
      top: "16px",
      right: "16px",
      backgroundColor: "#E53935",
      color: "#FFFFFF",
      width: "28px",
      height: "28px",
      padding: 0,
      "&:hover": {
        backgroundColor: "#c62828",
      },
    },
    previewDialogCloseIcon: {
      fontSize: "18px",
    },
    previewDialogTitle: {
      fontFamily: "Inter, sans-serif",
      fontWeight: 700,
      fontSize: "22px",
      color: "#000000",
      textAlign: "center",
      marginBottom: "20px",
      [theme.breakpoints.down("sm")]: {
        fontSize: "18px",
        marginBottom: "14px",
        paddingRight: "32px",
        paddingLeft: "32px",
      },
    },
    previewDialogContentBox: {
      border: "2px dashed #9E9E9E",
      borderRadius: "12px",
      backgroundColor: "#FFFFFF",
      padding: "28px 32px",
      fontFamily: "Inter, sans-serif",
      fontSize: "18px",
      lineHeight: "1.8",
      color: "#000000",
      [theme.breakpoints.down("sm")]: {
        padding: "16px",
        fontSize: "14px",
        lineHeight: "1.6",
        borderRadius: "10px",
      },
    },
  })
);

const initialValues: RewardCampaignInput = {
  startDateUtcMilli: 0,
  endDateUtcMilli: 0,
  campaignName: "",
  prizeDescription: "",
  sponsoringChamberMemberId: "",
};

const CreateChamberRewardCampaign = (): ReactElement => {
  const classes = useStyles();
  const enumStrings = useEnumStrings();
  const CHARACTER_LIMIT_DESCRIPTION = 250;

  const [loader, setLoader] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [customError, setCustomError] = useState(
    enumStrings.shopLocal.updateChamberShopLocalCampaign.anErrorOccurred
  );
  const [invalidError, setInvalidError] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showAlreadyActiveDialog, setShowAlreadyActiveDialog] = useState(false);
  const [createRewardCampaign] = useCreateRewardCampaignMutation();
  const redirectToRewardCampaignList = useRedirect(
    RedirectUri.activeChamberReward,
    {
      showToast: true,
    }
  );

  const chamberInfo = useGetChamberFromUserIdInAuth();

  const validationSchema = yup.object({
    sponsoringChamberMemberId: yup
      .string()
      .required(
        enumStrings.createChamberRewardCampaignValidation
          .sponsoringChamberMemberId
      ),
    campaignName: yup
      .string()
      .required(enumStrings.createChamberRewardCampaignValidation.campaignName),
    prizeDescription: yup
      .string()
      .required(
        enumStrings.createChamberRewardCampaignValidation.prizeDescription
      ),
    startDateUtcMilli: yup
      .number()
      .typeError("Please select a valid start date")
      .required(
        enumStrings.createChamberRewardCampaignValidation.startDateUtcMilli
      )
      .min(
        1,
        enumStrings.createChamberRewardCampaignValidation.startDateUtcMilli
      ),
    endDateUtcMilli: yup
      .number()
      .typeError("Please select a valid end date")
      .required(
        enumStrings.createChamberRewardCampaignValidation.endDateUtcMilli
      )
      .min(
        Date.now(),
        enumStrings.createChamberRewardCampaignValidation.endDateUtcMilli
      ),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (input: RewardCampaignInput) => {
      setLoader(true);
      try {
        setButtonDisabled(true);

        const rewardCampaignCreation = await createRewardCampaign({
          variables: {
            input: {
              startDateUtcMilli: input.startDateUtcMilli,
              endDateUtcMilli: input.endDateUtcMilli,
              campaignName: input.campaignName,
              prizeDescription: input.prizeDescription,
              sponsoringChamberMemberId: input.sponsoringChamberMemberId,
            },
          },
          refetchQueries: [
            { query: GetAllRewardCampaignsForChamberDocument },
            "getAllRewardCampaignsForChamber",
          ],
          awaitRefetchQueries: true,
        });
        const createionResult =
          rewardCampaignCreation.data?.createRewardCampaign;
        if (createionResult?.campaignCreatedSuccessfully === false) {
          setLoader(false);
          setButtonDisabled(false);
          setShowAlreadyActiveDialog(true);
          return;
        }

        setLoader(false);
        setShowSuccessDialog(true);
      } catch (error) {
        setInvalidError(true);
        setLoader(false);
        setButtonDisabled(false);
      }
    },
  });

  // Scroll to first validation error on submit
  useEffect(() => {
    if (formik.submitCount > 0 && Object.keys(formik.errors).length > 0) {
      const firstErrorField = document.querySelector('.Mui-error, .MuiFormHelperText-root.Mui-error');
      if (firstErrorField) {
        const container = firstErrorField.closest('.MuiFormControl-root') || firstErrorField;
        container.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.submitCount, formik.errors]);

  const createChamberRewardCampaignObj = useMemo(() => {
    // @ts-ignore
    return enumStrings.groups[UserType.ChamberAdmin][RedirectUri.createReward];
  }, [enumStrings.groups]);

  const { data: allChamberMemberData } = useGetAllChamberMembersQuery({
    variables: { chamberId: chamberInfo?.id || "" },
    skip: !chamberInfo?.id,
    fetchPolicy: "cache-and-network",
  });

  const [showLoader, setShowLoader] = useState(true);
  const [showStartDateWarning, setShowStartDateWarning] = useState(false);

  useEffect(() => {
    if (formik.values.startDateUtcMilli > 0) {
      setShowStartDateWarning(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.startDateUtcMilli]);

  useEffect(() => {
    if (allChamberMemberData?.getAllChamberMembers) {
      setShowLoader(false);
    }
  }, [allChamberMemberData?.getAllChamberMembers]);

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <h1 className={classes.pageTitle}>
          {createChamberRewardCampaignObj.title}
        </h1>
        <p className={classes.pageSubtitle}>
          {createChamberRewardCampaignObj.description}
        </p>
        <div className={classes.videoButtons}>
          <VideoTutorialButton
            tutorialUrl="https://share.synthesia.io/embeds/videos/2b53e93e-018e-4d54-9311-19f78ebacd9a"
          />
          <ExportButton
            customOnClick={() => openInstructionPdf("Create Rewards Contest.pdf")}
            disablePadding
            icon={
              <InfoOutlinedIcon
                style={{
                  color: theme.palette.primary.main,
                  marginRight: "10px",
                  width: "24px",
                  height: "24px",
                }}
              />
            }
            label="VIEW INSTRUCTIONS"
          />
        </div>
      </div>

      <div className={classes.contentContainer}>
        <div className={classes.contentCard}>
          {showLoader ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                paddingTop: 20,
              }}
            >
              <Loader />
            </div>
          ) : (
            <div className={classes.formFrame}>
              <form onSubmit={formik.handleSubmit} className={classes.form}>
            {/* Sponsoring Member Section */}
            <div className={classes.fieldContainer}>
              <div className={classes.fieldLabelRow}>
                <div className={classes.fieldLabel}>
                  {enumStrings.rewardsPage.campaignCompanySponsor}
                  <span className={classes.requiredAsterisk}>*</span>
                </div>
              </div>
              <p className={classes.helperText}>
                {enumStrings.rewardsPage.campaignCompanySponsorHelper}
              </p>
              <ChamberMemberSelector
                allChamberMemberData={allChamberMemberData}
                chamberMemberId={
                  formik.values.sponsoringChamberMemberId === ""
                    ? null
                    : formik.values.sponsoringChamberMemberId
                }
                setChamberMemberId={(chamberMemberId: string) => {
                  formik.setFieldValue(
                    "sponsoringChamberMemberId",
                    chamberMemberId || ""
                  );
                }}
                isRewardCampaignSelector
                hideTooltip
                hideLabel
                fullWidth
                error={!!((formik.touched.sponsoringChamberMemberId || formik.submitCount > 0) && formik.errors.sponsoringChamberMemberId)}
              />
              {(formik.touched.sponsoringChamberMemberId || formik.submitCount > 0) && formik.errors.sponsoringChamberMemberId && (
                <p style={{ color: "#f44336", fontSize: "12px", fontFamily: "Inter, sans-serif", margin: "4px 0 0 0" }}>
                  {formik.errors.sponsoringChamberMemberId}
                </p>
              )}
            </div>

            {/* Campaign Name Section */}
            <div className={classes.fieldContainer}>
              <div className={classes.fieldLabelRow}>
                <div className={classes.fieldLabel}>
                  {enumStrings.rewardsPage.rewardsCampaignName}
                  <span className={classes.requiredAsterisk}>*</span>
                </div>
              </div>
              <p className={classes.helperText} dangerouslySetInnerHTML={{ __html: enumStrings.rewardsPage.rewardsCampaignNameHelper }} />
              <TextField
                variant="outlined"
                className={`${classes.inputField} ${
                  formik.touched.campaignName && formik.errors.campaignName
                    ? classes.inputFieldError
                    : ""
                }`}
                placeholder={enumStrings.rewardsPage.rewardsCampaignNamePlaceholder}
                name="campaignName"
                value={formik.values.campaignName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={!!((formik.touched.campaignName || formik.submitCount > 0) && formik.errors.campaignName)}
                helperText={(formik.touched.campaignName || formik.submitCount > 0) && formik.errors.campaignName ? formik.errors.campaignName : ""}
              />
            </div>

            {/* Prize Description Section */}
            <div className={classes.fieldContainer}>
              <div className={classes.fieldLabelRow}>
                <div className={classes.fieldLabel}>
                  {enumStrings.rewardsPage.rewardsCampaignPrizeDescription}
                  <span className={classes.requiredAsterisk}>*</span>
                </div>
              </div>
              <p className={classes.helperText}>
                {enumStrings.rewardsPage.rewardsCampaignPrizeDescriptionHelper}
              </p>
              <TextField
                variant="outlined"
                className={`${classes.inputField} ${
                  formik.touched.prizeDescription &&
                  formik.errors.prizeDescription
                    ? classes.inputFieldError
                    : ""
                }`}
                placeholder={enumStrings.rewardsPage.rewardsCampaignPrizeDescriptionPlaceholder}
                multiline
                rows={4}
                name="prizeDescription"
                value={formik.values.prizeDescription}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                inputProps={{
                  maxLength: CHARACTER_LIMIT_DESCRIPTION,
                }}
                error={!!((formik.touched.prizeDescription || formik.submitCount > 0) && formik.errors.prizeDescription)}
                helperText={(formik.touched.prizeDescription || formik.submitCount > 0) && formik.errors.prizeDescription ? formik.errors.prizeDescription : ""}
              />
              <div className={classes.charCounter}>
                {(formik.values.prizeDescription || "").length}/
                {CHARACTER_LIMIT_DESCRIPTION}{" "}
                {enumStrings.shopLocal.createShopLocalCampaign.charRemaining}
              </div>

              {/* Preview Prize Description Button */}
              <Button
                onClick={() => setShowPreview(true)}
                className={classes.previewButton}
              >
                {enumStrings.rewardsPage.previewPrizeDescription}
              </Button>

              <Dialog
                open={showPreview}
                onClose={() => setShowPreview(false)}
                classes={{ paper: classes.previewDialogPaper }}
                maxWidth="sm"
                fullWidth
              >
                <IconButton
                  aria-label="close"
                  className={classes.previewDialogClose}
                  onClick={() => setShowPreview(false)}
                >
                  <CloseIcon className={classes.previewDialogCloseIcon} />
                </IconButton>
                <div className={classes.previewDialogTitle}>
                  {enumStrings.rewardsPage.rewardsPrizeDescription}
                </div>
                <div className={classes.previewDialogContentBox}>
                  {enumStrings.rewardsPage.descriptionText1}
                  <div style={{ marginTop: "12px" }}>
                    <strong>
                      {formik.values.prizeDescription.length > 0
                        ? formik.values.prizeDescription
                        : enumStrings.rewardsPage.enterText}
                    </strong>
                    .
                  </div>
                  <div style={{ marginTop: "16px" }}>
                    {enumStrings.rewardsPage.shareMoreWinChances}
                  </div>
                </div>
              </Dialog>
            </div>

            {/* Date Fields Section */}
            <div className={classes.fieldContainer}>
              <div className={classes.fieldLabelRow}>
                <div className={classes.fieldLabel}>
                  {enumStrings.rewardsPage.campaignDuration}
                  <span className={classes.requiredAsterisk}>*</span>
                </div>
              </div>
              <p className={classes.helperText} dangerouslySetInnerHTML={{ __html: enumStrings.rewardsPage.campaignDurationHelper }} />
              <div className={classes.dateFieldsContainer}>
                <div className={classes.dateFieldWrapper}>
                  <div
                    className={classes.fieldLabel}
                    style={{ marginBottom: "8px", fontSize: "14px" }}
                  >
                    {enumStrings.rewardsPage.rewardsCampaignStartDate}
                    <span className={classes.requiredAsterisk}>*</span>
                  </div>
                  <RewardCampaignEndDatePicker
                    formikErrors={formik.errors.startDateUtcMilli}
                    formikVal={formik.values.startDateUtcMilli}
                    formikSetFieldValue={formik.setFieldValue}
                    startAfterDate={Date.now()}
                    isForStartDate
                    fullWidth
                  />
                </div>
                <div className={classes.dateFieldWrapper}>
                  <div
                    className={classes.fieldLabel}
                    style={{ marginBottom: "8px", fontSize: "14px" }}
                  >
                    {enumStrings.rewardsPage.rewardsCampaignEndDate}
                    <span className={classes.requiredAsterisk}>*</span>
                  </div>
                  <div
                    onClick={() => {
                      if (!(formik.values.startDateUtcMilli > 0)) {
                        setShowStartDateWarning(true);
                        setTimeout(() => setShowStartDateWarning(false), 3000);
                      }
                    }}
                    style={{ pointerEvents: "auto" }}
                  >
                    <RewardCampaignEndDatePicker
                      formikErrors={formik.errors.endDateUtcMilli}
                      formikVal={formik.values.endDateUtcMilli}
                      formikSetFieldValue={formik.setFieldValue}
                      disabled={!(formik.values.startDateUtcMilli > 0)}
                      startAfterDate={formik.values.startDateUtcMilli}
                      fullWidth
                    />
                  </div>
                  {showStartDateWarning && (
                    <p style={{ color: "#f44336", fontSize: "12px", fontFamily: "Inter, sans-serif", margin: "4px 0 0 0" }}>
                      {enumStrings.rewardsPage.pleaseSelectStartDateFirst}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Error Messages */}
            {invalidError && (
              <div className={classes.invalidError}>{customError}</div>
            )}

            {Object.keys(formik?.errors || {}).length > 0 &&
              Object.keys(formik?.touched || {}).length > 0 && (
                <div className={classes.invalidError}>
                  {enumStrings.ChamberMemberSelector.pleaseAddressAnyIssues}
                </div>
              )}

                {/* Submit Button */}
                <Button
                  type="submit"
                  className={classes.submitButton}
                  disabled={buttonDisabled || loader}
                >
                  {loader ? (
                    <CircularProgress size={24} style={{ color: "white" }} />
                  ) : (
                    enumStrings.rewardsPage.create
                  )}
                </Button>
              </form>
            </div>
          )}
        </div>
      </div>

      {/* Success Dialog */}
      <ActionDialog
        open={showSuccessDialog}
        onClose={() => {
          setShowSuccessDialog(false);
          redirectToRewardCampaignList();
        }}
        iconVariant="success"
        title={enumStrings.rewardsPage.successDetailsSentSuccessfully}
        description={enumStrings.rewardsPage.rewardsCampaignCreatedSuccessfully}
        autoDismissMs={5000}
      />

      {/* Already Active Dialog */}
      <ActionDialog
        open={showAlreadyActiveDialog}
        onClose={() => setShowAlreadyActiveDialog(false)}
        iconVariant="error"
        title={enumStrings.rewardsPage.rewardsAlreadyExists}
        description={enumStrings.rewardsPage.rewardsCampaignAlreadyActive}
      />
    </div>
  );
};

export default CreateChamberRewardCampaign;
